// src/init-modules.ts
import { setDefaultAssets } from "@leagueplatform/asset-config";

// src/asset-map.ts
import chevronRight from "./assets/chevron-right.svg";
import warningIconOrange from "./assets/warning-icon-orange.svg";
var CLAIMS_ASSETS = {
  CHEVRON_RIGHT: chevronRight,
  WARNING_ICON_ORANGE: warningIconOrange
};

// src/init-modules.ts
var initModule = () => {
  setDefaultAssets(CLAIMS_ASSETS);
};

// src/components/recent-claims/recent-claims.component.tsx
import React from "react";
import { useIntl as useIntl2 } from "@leagueplatform/locales";
import {
  useNavigateLegacy as useNavigate,
  LinkLegacy as RouteLink
} from "@leagueplatform/routing";
import {
  Box,
  Flex,
  HeadingThree,
  Link,
  Overline,
  BodyTwo,
  Image,
  genesisStyled,
  css
} from "@leagueplatform/genesis-commons";
import { Icon } from "@leagueplatform/genesis-core";
import {
  MORE_INFORMATION_NEEDED,
  SUBMITTED,
  APPROVED,
  REJECTED,
  PAID
} from "@leagueplatform/web-common";
import { EVENT_NAME, trackAnalyticsEvent } from "@leagueplatform/analytics";
import {
  StatusBadge,
  STATUS_OPTIONS
} from "@leagueplatform/web-common-components";
import { handleStaticAsset } from "@leagueplatform/asset-config";

// src/hooks/queries/use-claim-status-message-translation.ts
import { useIntl } from "@leagueplatform/locales";
var useClaimStatusMessageTranslation = () => useIntl().$tmap({
  started: "STARTED",
  submitted_for_preapproval: "SUBMITTED_FOR_PREAPPROVAL",
  preapproval_more_information_needed: "PREAPPROVAL_MORE_INFORMATION_NEEDED",
  preapproved: "PREAPPROVED",
  preapproval_rejected: "PREAPPROVAL_REJECTED",
  more_information_needed: "MORE_INFORMATION_NEEDED",
  pending_external_review: "PENDING_EXTERNAL_REVIEW",
  submitted: "SUBMITTED",
  approved: "APPROVED",
  rejected: "REJECTED",
  cancelled: "CANCELLED",
  paid: "PAID"
});

// src/hooks/queries/use-claims-query.hook.ts
import { useQuery } from "@tanstack/react-query";
import {
  getClaims,
  GET_CLAIMS_MESSAGE_TYPE
} from "@leagueplatform/claims-b2b-api";
function useClaimsQuery(userId, options) {
  return useQuery(
    [GET_CLAIMS_MESSAGE_TYPE, userId],
    () => getClaims(userId),
    options
  );
}

// src/components/recent-claims/recent-claims.component.tsx
var noDataPlaceholder = "---";
var StyledBodyTwo = genesisStyled(BodyTwo)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
var ClaimsBox = genesisStyled(Box)(
  css({
    overflowX: "auto",
    "&:focus": {
      boxShadow: "rgba(0, 0, 0, 0.5)",
      outline: "interactive.focus.outer.outer"
    }
  })
);
var CLAIMS_HISTORY_URL = "/member/claims-history";
var CLAIMS_HEADING_ID = "recent-claims-section";
var getClaimUrl = (claimId) => `/member/claims/${claimId}?source=wallet`;
var mapClaimStatus = (claimStatus) => {
  switch (claimStatus) {
    case MORE_INFORMATION_NEEDED:
      return STATUS_OPTIONS.WARNING;
    case SUBMITTED:
      return STATUS_OPTIONS.HIGHLIGHT;
    case APPROVED:
      return STATUS_OPTIONS.HIGHLIGHT;
    case REJECTED:
      return STATUS_OPTIONS.CRITICAL;
    case PAID:
      return STATUS_OPTIONS.SUCCESS;
    default:
      return STATUS_OPTIONS.DISABLED;
  }
};
var RecentClaims = ({ userId }) => {
  const { data: { claims = [] } = {} } = useClaimsQuery(userId);
  const navigate = useNavigate();
  const { formatMessage, $tifelse, formatDate, formatMoney } = useIntl2();
  const needMoreInfo = claims.filter(
    (claim) => claim.status === MORE_INFORMATION_NEEDED
  ).length;
  function onClaimClick(status) {
    trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
      category: "Wallet",
      action: "View Claim Detail",
      label: status
    });
  }
  const getClaimStatusMessage = useClaimStatusMessageTranslation();
  return /* @__PURE__ */ React.createElement(Box, null, /* @__PURE__ */ React.createElement(Flex, { justifyContent: "space-between" }, /* @__PURE__ */ React.createElement(HeadingThree, { id: CLAIMS_HEADING_ID, as: "h2" }, formatMessage({ id: "RECENT_CLAIMS" })), /* @__PURE__ */ React.createElement(
    Link,
    {
      as: RouteLink,
      to: CLAIMS_HISTORY_URL,
      onClick: () => {
        trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
          category: "Wallet",
          action: "View All Claims"
        });
      }
    },
    formatMessage({ id: "VIEW_ALL_CLAIMS" })
  )), needMoreInfo > 0 && /* @__PURE__ */ React.createElement(
    Flex,
    {
      marginTop: "oneAndHalf",
      marginBottom: "one",
      paddingY: "half",
      paddingX: "one",
      backgroundColor: "warning.background.subdued",
      borderStyle: "solid",
      borderWidth: "thin",
      borderColor: "warning.border.default",
      borderRadius: "medium",
      alignItems: "center"
    },
    /* @__PURE__ */ React.createElement(
      Image,
      {
        src: handleStaticAsset("WARNING_ICON_ORANGE" /* WARNING_ICON_ORANGE */),
        role: "presentation",
        height: "oneAndHalf",
        width: "oneAndHalf",
        marginInlineEnd: "one"
      }
    ),
    /* @__PURE__ */ React.createElement(Box, null, /* @__PURE__ */ React.createElement(BodyTwo, { fontWeight: "medium", display: "inline" }, formatMessage({ id: "PLEASE_NOTE" }), ":"), /* @__PURE__ */ React.createElement(BodyTwo, { marginInlineStart: "quarter", display: "inline" }, $tifelse(
      needMoreInfo > 1,
      "NEED_MORE_INFO_MULTIPLE_CLAIMS",
      "NEED_MORE_INFO_SINGLE_CLAIM"
    )))
  ), /* @__PURE__ */ React.createElement(ClaimsBox, { role: "region", "aria-labelledby": CLAIMS_HEADING_ID, tabIndex: 0 }, /* @__PURE__ */ React.createElement(
    Box,
    {
      as: "table",
      width: 1,
      marginTop: "one",
      "aria-labelledby": CLAIMS_HEADING_ID
    },
    /* @__PURE__ */ React.createElement("thead", null, /* @__PURE__ */ React.createElement(
      Box,
      {
        as: "tr",
        textAlign: "start",
        padding: "one",
        paddingTop: "none",
        borderBottomStyle: "solid",
        borderBottomWidth: "thin"
      },
      /* @__PURE__ */ React.createElement(
        Overline,
        {
          as: "th",
          scope: "col",
          color: "onSurface.text.subdued",
          padding: "one",
          minWidth: 136
        },
        formatMessage({ id: "STATUS" })
      ),
      /* @__PURE__ */ React.createElement(
        Overline,
        {
          as: "th",
          scope: "col",
          color: "onSurface.text.subdued",
          padding: "one",
          maxWidth: 200
        },
        formatMessage({ id: "CLAIM_REF_NUMBER" })
      ),
      /* @__PURE__ */ React.createElement(
        Overline,
        {
          as: "th",
          scope: "col",
          color: "onSurface.text.subdued",
          padding: "one",
          maxWidth: 200
        },
        formatMessage({ id: "VENDOR" })
      ),
      /* @__PURE__ */ React.createElement(
        Overline,
        {
          as: "th",
          scope: "col",
          color: "onSurface.text.subdued",
          padding: "one"
        },
        formatMessage({ id: "LAST_UPDATED" })
      ),
      /* @__PURE__ */ React.createElement(
        Overline,
        {
          as: "th",
          scope: "col",
          color: "onSurface.text.subdued",
          padding: "one",
          textAlign: "end"
        },
        formatMessage({ id: "AMOUNT" })
      ),
      /* @__PURE__ */ React.createElement(Box, { as: "th", padding: "one" })
    )),
    /* @__PURE__ */ React.createElement("tbody", null, claims.map((claim, index) => {
      const amount = claim.amount_approved || claim.amount_claimed || {};
      const hasAmount = amount && !!amount.value;
      const CLAIM_URL = getClaimUrl(claim.claim_id);
      const badgeStatus = mapClaimStatus(claim.status);
      return /* @__PURE__ */ React.createElement(
        Box,
        {
          as: "tr",
          key: claim.claim_id,
          borderBottomStyle: index === claims.length - 1 ? "none" : "solid",
          borderBottomWidth: "thin",
          cursor: "pointer",
          hoverStyle: {
            backgroundColor: "surface.background.secondary"
          },
          onClick: () => {
            navigate(CLAIM_URL);
            onClaimClick(claim.status);
          }
        },
        /* @__PURE__ */ React.createElement(
          Box,
          {
            as: "td",
            padding: "one",
            verticalAlign: "middle",
            minWidth: 136
          },
          /* @__PURE__ */ React.createElement(
            StatusBadge,
            {
              status: badgeStatus,
              label: getClaimStatusMessage(claim.status),
              displayIcon: true,
              smallText: true
            }
          )
        ),
        /* @__PURE__ */ React.createElement(
          StyledBodyTwo,
          {
            as: "td",
            padding: "one",
            maxWidth: 200,
            verticalAlign: "middle"
          },
          claim.reference_id || noDataPlaceholder
        ),
        /* @__PURE__ */ React.createElement(
          StyledBodyTwo,
          {
            as: "td",
            padding: "one",
            maxWidth: 200,
            verticalAlign: "middle"
          },
          claim.vendor || noDataPlaceholder
        ),
        /* @__PURE__ */ React.createElement(BodyTwo, { as: "td", padding: "one", verticalAlign: "middle" }, formatDate(claim.last_event_timestamp, {
          year: "numeric",
          month: "long",
          day: "numeric"
        })),
        /* @__PURE__ */ React.createElement(
          BodyTwo,
          {
            as: "td",
            padding: "one",
            verticalAlign: "middle",
            textAlign: "end"
          },
          hasAmount ? formatMoney({ F: amount.value, C: amount.currency }) : noDataPlaceholder
        ),
        /* @__PURE__ */ React.createElement(Box, { as: "td", padding: "one", verticalAlign: "middle" }, /* @__PURE__ */ React.createElement(
          Link,
          {
            as: RouteLink,
            to: CLAIM_URL,
            onClick: () => {
              onClaimClick(claim.status);
            },
            "aria-label": formatMessage(
              { id: "VIEW_CLAIM_LABEL_HIDDEN" },
              {
                vendor: claim.vendor,
                referenceId: claim.reference_id
              }
            )
          },
          /* @__PURE__ */ React.createElement(
            Icon,
            {
              icon: "interfaceChevronForward",
              size: "$one",
              css: {
                verticalAlign: "text-top"
              }
            }
          )
        ))
      );
    }))
  )));
};

// src/index.ts
initModule();
export {
  RecentClaims,
  useClaimsQuery
};


  if (typeof window !== 'undefined' && !(typeof process !== 'undefined' && process.env?.NODE_ENV === 'test')) {
    window.leagueSDKsPresent = window.leagueSDKsPresent || [];
    window.leagueSDKsPresent['@leagueplatform/claims-b2b'] = window.leagueSDKsPresent['@leagueplatform/claims-b2b'] || [];
    window.leagueSDKsPresent['@leagueplatform/claims-b2b'].push('2.28.1');

    const numInstances = window.leagueSDKsPresent['@leagueplatform/claims-b2b'].length

    if (numInstances > 1) {
      console.error('Multiple copies (' + numInstances + ') of the @leagueplatform/claims-b2b module are loaded! This can cause issues. Ensure that your project bundle only contains one copy/version of each League SDK package.');
    }
  }
  
//# sourceMappingURL=index.js.map